import React from 'react';
import Icon from '../AppIcon';

const SYNC_CONFIG = {
  synced: {
    label: 'Synced with Project Calculator',
    icon: 'CheckCircle2',
    className: 'bg-success/10 text-success border-success/30',
    iconClass: 'text-success',
    dot: 'bg-success',
  },
  pending_update: {
    label: 'Pending Update',
    icon: 'RefreshCw',
    className: 'bg-warning/10 text-warning border-warning/30',
    iconClass: 'text-warning',
    dot: 'bg-warning',
  },
  approval_required: {
    label: 'Approval Required',
    icon: 'AlertCircle',
    className: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
    iconClass: 'text-orange-400',
    dot: 'bg-orange-400',
  },
};

/**
 * SyncStatusBadge — shows the live sync relationship between
 * an Incentive Ticket and the Project Calculator.
 *
 * Props:
 *   syncStatus: 'synced' | 'pending_update' | 'approval_required'
 *   lastSyncedAt: ISO date string
 *   isAdjustment: boolean — true if this is an adjustment ticket
 *   parentTicketNumber: string — ticket number of the original (for adjustment tickets)
 *   onApplyUpdate: function — called when user clicks "Apply Update" (pending_update)
 *   canApplyUpdate: boolean — whether the current user can apply the update
 */
const SyncStatusBadge = ({
  syncStatus = 'synced',
  lastSyncedAt,
  isAdjustment = false,
  parentTicketNumber,
  onApplyUpdate,
  canApplyUpdate = false,
}) => {
  const config = SYNC_CONFIG?.[syncStatus] || SYNC_CONFIG?.synced;

  const formattedDate = lastSyncedAt
    ? new Date(lastSyncedAt)?.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <div className="flex flex-col gap-2">
      {/* Main badge */}
      <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-medium ${config?.className}`}>
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${config?.dot}`} />
        <Icon name={config?.icon} size={13} className={config?.iconClass} />
        <span>✓ {config?.label}</span>
      </div>

      {/* Last sync timestamp */}
      {formattedDate && (
        <p className="text-xs text-muted-foreground pl-1">
          Last Sync: <span className="font-medium text-foreground">{formattedDate}</span>
        </p>
      )}

      {/* Adjustment ticket notice */}
      {isAdjustment && parentTicketNumber && (
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-500/10 border border-blue-500/20 rounded-xl text-xs text-blue-400">
          <Icon name="GitBranch" size={13} />
          <span>Adjustment for <span className="font-mono font-bold">{parentTicketNumber}</span></span>
        </div>
      )}

      {/* Apply update button for pending_update status */}
      {syncStatus === 'pending_update' && canApplyUpdate && onApplyUpdate && (
        <button
          onClick={onApplyUpdate}
          className="inline-flex items-center gap-2 px-3 py-2 bg-warning/10 hover:bg-warning/20 border border-warning/30 rounded-xl text-xs font-medium text-warning transition-colors"
        >
          <Icon name="RefreshCw" size={13} />
          Apply Calculator Update & Re-Submit
        </button>
      )}

      {/* Info for pending_update when user can't apply */}
      {syncStatus === 'pending_update' && !canApplyUpdate && (
        <p className="text-xs text-warning pl-1">
          Calculator values have changed. An approver must apply the update.
        </p>
      )}
    </div>
  );
};

export default SyncStatusBadge;
